import type { CableProvenanceSource } from "../../utils/fetchCables";
import type { CircuitBoardMarker } from "../../../ReviewPage/FloorPlanPanelNew/SvgOverlay/CircuitPathsLayer/types";
import {
  DEFAULT_SCALE_PIXELS_PER_METER,
  ESTIMATED_PLACEMENT_PATTERN,
  RISER_PATTERN,
} from "./constants";
import type {
  ComputeCableScalePixelsPerMeterArgs,
  DecorateSourceMarkersArgs,
  SourceConfidence,
} from "./types";

const normalizeBoardName = (name: string): string => name.trim().toLowerCase();

/** Whether the agent only ESTIMATED this source's position (explicit flag first,
 * text heuristic as a fallback for older cables.json). */
const isEstimatedSource = (source: CableProvenanceSource): boolean =>
  source.estimated ??
  (!!source.placed_by && ESTIMATED_PLACEMENT_PATTERN.test(source.placed_by));

/** Lowercased board names the agent only ESTIMATED (no real board symbol / text). */
export const computeUncertainSourceNames = (
  sources: CableProvenanceSource[],
): Set<string> => {
  const uncertainNames = new Set<string>();
  for (const source of sources) {
    if (isEstimatedSource(source)) {
      uncertainNames.add(normalizeBoardName(source.name));
    }
  }
  return uncertainNames;
};

/** 3-level source placement confidence keyed by lowercased board name.
 * estimated → "estimated"; plan_text placed at the riser entry → "riser"
 * (panel known, dot is the riser, not the panel); otherwise → "confident". */
export const computeSourceConfidenceByName = (
  sources: CableProvenanceSource[],
): Record<string, SourceConfidence> => {
  const confidenceByBoardName: Record<string, SourceConfidence> = {};
  for (const source of sources) {
    const boardNameKey = normalizeBoardName(source.name);
    if (isEstimatedSource(source)) {
      confidenceByBoardName[boardNameKey] = "estimated";
    } else if (
      source.origin === "plan_text" &&
      !!source.placed_by &&
      RISER_PATTERN.test(source.placed_by)
    ) {
      confidenceByBoardName[boardNameKey] = "riser";
    } else {
      confidenceByBoardName[boardNameKey] = "confident";
    }
  }
  return confidenceByBoardName;
};

/** Decorate the agent's source markers with `uncertain` + `confidence` so the
 * plan overlay can style estimated/riser placements distinctly. Returns the raw
 * markers unchanged when there is nothing to decorate. */
// "SIBE · Sicherheitsbeleuchtung" reads as SIBE on the plan; a name too long for a marker keeps the short label.
const BOARD_DISPLAY_MAX = 12;

export const boardDisplayName = ({ name, label }: { name: string; label: string }): string => {
  const head = name.split("·")[0].trim();
  if (!head || head.length > BOARD_DISPLAY_MAX) return label;
  return head;
};

export const decorateSourceMarkers = ({
  sources,
  uncertainSourceNames,
  confidenceByBoardName,
  sourceNames,
}: DecorateSourceMarkersArgs): CircuitBoardMarker[] | undefined => {
  if (!sources) return sources;
  return sources.map((source) => {
    const boardNameKey = normalizeBoardName(source.name);
    const confidence = confidenceByBoardName[boardNameKey];
    return {
      ...source,
      // Display only: selection hit-tests the raw geometry source, so its key is unaffected.
      label: sourceNames[source.label] ?? boardDisplayName(source),
      // Back-compat: `uncertain` stays true only for estimated placements.
      uncertain: uncertainSourceNames.has(boardNameKey),
      ...(confidence ? { confidence } : {}),
    };
  });
};

/** Cable lengths use the SAME page scale as Kabelgruppen (metres per PDF point);
 * fall back to the agent's default px/m only when the page is uncalibrated. */
export const computeCableScalePixelsPerMeter = ({
  pageScale,
  imageScaleFactor,
}: ComputeCableScalePixelsPerMeterArgs): number =>
  pageScale && pageScale > 0
    ? imageScaleFactor / pageScale
    : DEFAULT_SCALE_PIXELS_PER_METER;
